import type { User } from '@pay-plan/shared';

export const PROFILE_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

function cacheKey(accountId: string): string {
  return `payplan:user:${accountId}`;
}

interface CachedPayload {
  user?: User;
  cachedAt?: number;
}

/**
 * Reads a cached user profile for the given MSAL local account. Returns
 * null when absent, corrupted, expired, or when sessionStorage itself
 * throws (private-mode Safari, exceeded quota). Callers treat the cache
 * as a perf hint — never a correctness input — so all failure modes
 * collapse to "no cache."
 */
export function loadCachedUser(accountId: string): User | null {
  try {
    const raw = sessionStorage.getItem(cacheKey(accountId));
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as CachedPayload;
    if (
      parsed.user === undefined ||
      parsed.cachedAt === undefined ||
      Date.now() - parsed.cachedAt > PROFILE_CACHE_TTL_MS
    ) {
      return null;
    }
    return parsed.user;
  } catch {
    return null;
  }
}

export function storeCachedUser(accountId: string, user: User): void {
  try {
    sessionStorage.setItem(cacheKey(accountId), JSON.stringify({ user, cachedAt: Date.now() }));
  } catch {
    // sessionStorage can throw in private-mode Safari or when quota-exceeded.
    // Cache is a perf optimization, not a correctness requirement — swallow.
  }
}

export function clearCachedUser(accountId: string): void {
  try {
    sessionStorage.removeItem(cacheKey(accountId));
  } catch {
    /* ignore */
  }
}
